import { useState, useEffect, useMemo } from 'react';
import { AmiraResources } from '@/models/AmiraResources.interface';
import { ResourceType } from '@/enums/ResourceType';
import { skillResourceLicenses } from '@/constants/skillResourceLicenses';
import { LessonDetails } from '@/models/LessonDetails';

interface Student {
  assignment?: {
    manifest?: { storyId: string; activityType?: string }[];
    resources?: string[];
  };
  student?: { id: string };
}

interface InputState {
  assignmentsType: string;
  providers: string;
}

const nilUUID = "00000000-0000-0000-0000-000000000000";

export function useFilteredResources(
  skillResources: AmiraResources[] | undefined,
  students: Student[],
  lesson: LessonDetails,
  instruct: boolean,
  tutor: boolean,
  inputState: InputState,
  assignmentsTypeNoFilterValue: string,
  providersNoFilterValue: string,
  currentlyAssignedStories: AmiraResources[],
  newAssignments: AmiraResources[]
) {
  // Available resources (after removing assigned ones but before user input filters)
  const availableResources = useMemo(() => {
    if (!skillResources) return [];

    const assignedStoryIds = new Set<string>();
    const assignedTeacherResourcesIds = new Set<string>();
    let isSkillTutorAssigned = false;

    students.forEach(student => {
      student.assignment?.manifest?.forEach(manifestItem => {
        if (manifestItem.storyId === nilUUID) {
          isSkillTutorAssigned = true;
        }
        assignedStoryIds.add(manifestItem.storyId);
      });
    });

    if (students.length === 1) {
      students[0].assignment?.resources?.forEach((resourceId: string) => {
        assignedTeacherResourcesIds.add(resourceId);
      });
    } else {
      lesson?.resources?.forEach(resourceGroup => {
        resourceGroup.resources.forEach(resource => assignedTeacherResourcesIds.add(resource.resourceUniqueId));
      });
    }

    let resourcesToFilter = skillResources.filter(resource => {
      const isAssignedById = assignedStoryIds.has(resource.resourceUniqueId);
      const isSkillTutorType = resource.resourceType === ResourceType.skillTutor;
      const isAssignedTeacherResource = assignedTeacherResourcesIds.has(resource.resourceUniqueId);
      return !isAssignedById && !(isSkillTutorType && isSkillTutorAssigned) && !isAssignedTeacherResource;
    });

    const entitledResources = resourcesToFilter.filter(resource => {
      const resourceType = resource.resourceType as ResourceType;
      if (skillResourceLicenses.others.includes(resourceType)) return true;
      if (instruct && skillResourceLicenses.instruct.includes(resourceType)) return true;
      if (tutor && skillResourceLicenses.tutor.includes(resourceType)) return true;
      return false;
    });

    const currentlyAssignedIds = new Set(currentlyAssignedStories.map(r => r.resourceUniqueId));
    const newAssignmentIds = new Set(newAssignments.map(r => r.resourceUniqueId));

    return entitledResources.filter(
      resource => !currentlyAssignedIds.has(resource.resourceUniqueId) &&
                 !newAssignmentIds.has(resource.resourceUniqueId)
    );
  }, [
    skillResources,
    students,
    lesson,
    instruct,
    tutor,
    currentlyAssignedStories,
    newAssignments
  ]);

  // Final filtered resources (after applying user input filters)
  const finalFilteredResources = useMemo(() => {
    let filtered = availableResources;

    if (inputState.assignmentsType !== assignmentsTypeNoFilterValue) {
      filtered = filtered.filter(resource =>
        resource.resourceType === inputState.assignmentsType
      );
    }
    if (inputState.providers !== providersNoFilterValue) {
      filtered = filtered.filter(resource =>
        resource.provider === inputState.providers
      );
    }

    return filtered;
  }, [availableResources, inputState, assignmentsTypeNoFilterValue, providersNoFilterValue]);

  return {
    filteredResources: finalFilteredResources,
    availableResources // This is what should be used for filter options
  };
}